/**
 * The Outliner (SPEC §3, §11): the open level as a keyboard-navigable ARIA
 * tree with search, type column and counts. It follows the shared selection
 * (clicking in the viewport reveals and highlights the entity here) and
 * selects through `selection.set`, never by touching selection state.
 *
 * Expansion, focus and the search text are presentational React state
 * (architecture §8); what's visible is computed by `outlinerModel`. Each row
 * starts with a visibility eye, as in Unreal's Outliner, so the type label
 * keeps the right edge. Names rename in place with F2 or a slow second click
 * on a selected row (Unreal's triggers); double-click still frames. A row's
 * context menu (right-click, the context-menu key or Shift+F10) offers the
 * same actions.
 */
import { usePointerKind } from "../hooks/usePointerKind";
import { useEffect, useMemo, useRef, useState } from "react";
import type { DragEvent, KeyboardEvent } from "react";
import type { EntityId, EntityRecord } from "../../world/entity";
import type { SelectionState } from "../selection/Selection";
import { useRename } from "../bindings/useRename";
import { clickMode, selectionAfterClick } from "../selection/selectionModel";
import type { ModifierKeys } from "../selection/selectionModel";
import { actionTargets, setVisibleEdit, visibilityOf } from "../selection/selectionEdits";
import { isContextMenuKey, menuPointFor, useContextMenu } from "../ui/ContextMenu";
import type { ContextMenuHandle } from "../ui/ContextMenu";
import type { MenuItem } from "../ui/Menu";
import { CREATE_PRESETS, EMPTY_PRESET } from "../create/createModel";
import { entityEditItems, useEntityEdits } from "../selection/entityEdits";
import { cameraMenuItems } from "../viewport/cameraMenuItems";
import type { EntityEdits } from "../selection/entityEdits";
import { useCreate } from "../create/useCreate";
import { runEdit, useEditor } from "../EditorContext";
import { useSelection, useWorldSnapshot } from "../hooks/editorHooks";
import {
  ChevronDownIcon,
  ChevronRightIcon,
  EntityTypeIcon,
  EyeIcon,
  EyeOffIcon,
  PlusIcon,
  SearchIcon,
} from "../ui/icons";
import { dropDestination, dropZone, keyboardMove, moveEdit } from "./dropModel";
import type { Destination, DropZone } from "./dropModel";
import {
  ancestorsOf,
  initialExpansion,
  openNewParents,
  treeKeyAction,
  visibleRows,
} from "./outlinerModel";
import type { OutlinerRow } from "./outlinerModel";
import { useTourTarget } from "../tour/TourTargets";
import "./outliner.css";

export function OutlinerPanel() {
  const [filter, setFilter] = useState("");
  const menu = useContextMenu();
  const tree = useOutlinerTree(filter, menu.open);
  const create = useCreate("outliner");
  const tourTarget = useTourTarget("outliner");

  // Unity's Hierarchy + menu, and Unreal's Place Actors, in short.
  const openCreateMenu = (at: { x: number; y: number }) => {
    menu.open(
      at,
      "Create",
      CREATE_PRESETS.map((preset) => ({
        label: preset.label,
        run: () => {
          create(preset);
        },
      })),
    );
  };

  return (
    <div ref={tourTarget} className="kn-outliner" data-testid="outliner">
      <div className="kn-outliner__toolbar">
        <button
          type="button"
          className="kn-icon-button"
          aria-label="Create"
          title="Create entity"
          aria-haspopup="menu"
          onClick={(event) => {
            openCreateMenu(menuPointFor(event.currentTarget));
          }}
        >
          <PlusIcon size={12} />
        </button>
        <label className="kn-outliner__search">
          <SearchIcon size={12} />
          <input
            type="search"
            aria-label="Search entities"
            placeholder="Search entities"
            value={filter}
            onChange={(event) => {
              setFilter(event.target.value);
            }}
          />
        </label>
      </div>

      <div
        className="kn-outliner__tree"
        role="tree"
        aria-label="Entities"
        aria-multiselectable="true"
        onContextMenu={(event) => {
          // Rows open their own menu; empty space offers Create, as in Unity's Hierarchy.
          if (event.target instanceof Element && event.target.closest('[role="treeitem"]')) return;
          event.preventDefault();
          openCreateMenu({ x: event.clientX, y: event.clientY });
        }}
      >
        {tree.rows.length === 0 && (
          <p className="kn-outliner__empty">
            {filter ? "No matching entities" : "No level loaded"}
          </p>
        )}
        {tree.rows.map((row) => (
          <OutlinerItem key={row.id} row={row} tree={tree} />
        ))}
      </div>
      {menu.element}
    </div>
  );
}

// ─── Tree state ──────────────────────────────────────────────────────────

interface OutlinerTree {
  readonly rows: readonly OutlinerRow[];
  readonly selected: ReadonlySet<EntityId>;
  readonly focused: EntityId | null;
  readonly setFocused: (id: EntityId) => void;
  readonly toggle: (id: EntityId) => void;
  readonly select: (id: EntityId) => void;
  /** A row click with its modifiers: replace, Ctrl toggle, or Shift range (selectionModel). */
  readonly click: (id: EntityId, keys: ModifierKeys) => void;
  readonly frame: (id: EntityId) => void;
  /** A double-click: frames the row's entity, or takes a camera's view. */
  readonly activate: (id: EntityId) => void;
  readonly setVisible: (id: EntityId, visible: boolean) => void;
  /** The entity whose name is being edited in place, if any. */
  readonly renaming: EntityRecord | null;
  readonly startRename: (id: EntityId) => void;
  /** A click on an already-selected row's name: rename after a pause, unless it becomes a double-click. */
  readonly renameAfterPause: (id: EntityId) => void;
  readonly endRename: (id: EntityId) => void;
  readonly onKeyDown: (id: EntityId, event: KeyboardEvent) => void;
  /** Opens a row's context menu at a page point (selecting it, unless it's already selected). */
  /** Where a drag over the row would drop, for its indicator; null when not a target. */
  readonly dropZoneOf: (id: EntityId) => DropZone | null;
  readonly drag: RowDragHandlers;
  readonly openMenu: (row: OutlinerRow, at: { x: number; y: number }) => void;
  readonly registerRow: (id: EntityId, element: HTMLElement | null) => void;
}

function useOutlinerTree(filter: string, openContextMenu: ContextMenuHandle["open"]): OutlinerTree {
  const { components, dispatch, dispatchAll } = useEditor();
  const create = useCreate("outliner");
  const editsFor = useEntityEdits("outliner");
  const snapshot = useWorldSnapshot();
  const selection = useSelection();

  const [expanded, setExpanded] = useState<ReadonlySet<EntityId>>(() => initialExpansion(snapshot));
  const [focused, setFocused] = useState<EntityId | null>(selection.active);

  // Follow the level, the entities and the selection, adjusting state during render
  // (React's pattern for "derive from a changed input", which avoids an effect cascade).
  const [followed, setFollowed] = useState({
    level: snapshot.levelId,
    entities: snapshot.entities,
    active: selection.active,
  });
  if (
    followed.level !== snapshot.levelId ||
    followed.entities !== snapshot.entities ||
    followed.active !== selection.active
  ) {
    const newLevel = followed.level !== snapshot.levelId;
    const newActive = followed.active !== selection.active;
    setFollowed({ level: snapshot.levelId, entities: snapshot.entities, active: selection.active });

    // New hierarchies open; the path to a newly active entity opens. A parent the
    // visitor collapsed stays collapsed through other edits.
    const opened = openNewParents(expanded, followed.entities, snapshot.entities);
    setExpanded(
      newLevel
        ? initialExpansion(snapshot)
        : newActive
          ? reveal(opened, snapshot, selection.active)
          : opened,
    );
    if (selection.active !== null && newActive) setFocused(selection.active);
  }

  const rows = useMemo(
    () => visibleRows(snapshot, components, expanded, filter),
    [snapshot, components, expanded, filter],
  );
  const elements = useRowElements(selection.active);
  const rename = useRenameState();

  const setOpen = (id: EntityId, open: boolean) => {
    setExpanded((current) => {
      const next = new Set(current);
      if (open) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  /** Moves entities (entity.move keeps their world transforms) and opens the new parent. */
  const moveTo = (ids: readonly EntityId[], destination: Destination) => {
    runEdit({ dispatch, dispatchAll }, moveEdit(snapshot.entities, ids, destination, "outliner"));
    if (destination.parent !== null) setOpen(destination.parent, true);
  };
  const dnd = useRowDragAndDrop(snapshot.entities, selection, moveTo);
  const select = (id: EntityId) => {
    // Selecting another row abandons a pending slow-click rename.
    rename.cancelPending(id);
    anchor.current = id;
    void dispatch({ type: "selection.set", payload: { ids: [id] }, origin: "outliner" });
  };
  // Where Shift+click ranges start: the last plain or Ctrl click, like Windows Explorer.
  const anchor = useRef<EntityId | null>(selection.active);

  const click = (id: EntityId, keys: ModifierKeys) => {
    const mode = clickMode(keys, "range");
    if (mode === "replace") {
      select(id);
      return;
    }
    rename.cancelPending(null);
    const next = selectionAfterClick(
      selection,
      id,
      mode === "range"
        ? { mode, anchor: anchor.current, order: rows.map((row) => row.id) }
        : { mode },
    );
    if (mode === "toggle") anchor.current = id;
    void dispatch({
      type: "selection.set",
      payload: { ids: next.ids, ...(next.active !== null && { active: next.active }) },
      origin: "outliner",
    });
  };

  // A selected row's eye (or menu) takes the whole selection, as in Unreal's Outliner.
  const setVisible = (id: EntityId, visible: boolean) => {
    const targets = visibilityOf(snapshot.entities, actionTargets(selection, id));
    runEdit({ dispatch, dispatchAll }, setVisibleEdit(targets, visible, "outliner"));
  };
  const frame = (id: EntityId) => {
    rename.cancelPending(null);
    select(id);
    void dispatch({ type: "camera.frame", payload: {}, origin: "outliner" });
  };
  // Double-click frames, like Unreal's Outliner; on a camera it takes the camera's view
  // instead (Unity's Align View to Selected), which is why you'd double-click one.
  const activate = (id: EntityId) => {
    if (!snapshot.entities.get(id)?.components.has("Camera")) {
      frame(id);
      return;
    }
    rename.cancelPending(null);
    select(id);
    void dispatch({ type: "camera.alignTo", payload: { entity: id }, origin: "outliner" });
  };

  const openMenu = (row: OutlinerRow, at: { x: number; y: number }) => {
    // On a selected row the menu acts on the whole selection, so keep it.
    const count = selection.ids.includes(row.id) ? selection.ids.length : 1;
    if (count === 1) select(row.id);
    const frameSelection = () => {
      void dispatch({ type: "camera.frame", payload: {}, origin: "outliner" });
    };
    // What the menu acts on: the selection, or the row it's about to select.
    const acting = count > 1 ? selection : { ids: [row.id], active: row.id };
    const actions = {
      createChild: (id: EntityId) => {
        create(EMPTY_PRESET, { parent: id });
      },
      rename: rename.start,
      setVisible,
      frame: count > 1 ? frameSelection : frame,
      select,
    };
    const title = count > 1 ? `${count} entities` : row.name;
    // Camera actions apply to one camera at a time.
    const cameraItems =
      count === 1
        ? cameraMenuItems(snapshot.entities.get(row.id), (request) => {
            void dispatch({ ...request, origin: "outliner" });
          })
        : [];
    openContextMenu(at, title, rowMenuItems(row, count, actions, editsFor(acting), cameraItems));
  };

  return {
    rows,
    selected: new Set(selection.ids),
    // A filtered-out focus falls back to the first row, so the tree stays tabbable.
    focused: rows.some((row) => row.id === focused) ? focused : (rows[0]?.id ?? null),
    setFocused,
    toggle: (id) => {
      setOpen(id, !expanded.has(id));
    },
    select,
    click,
    setVisible,
    renaming: rename.id === null ? null : (snapshot.entities.get(rename.id) ?? null),
    startRename: rename.start,
    renameAfterPause: rename.startAfterPause,
    endRename: (id) => {
      rename.end();
      // Back to the row, so the keyboard carries on where it was.
      elements.focus(id);
    },
    frame,
    activate,
    openMenu,
    dropZoneOf: (id) => (dnd.over?.id === id ? dnd.over.zone : null),
    drag: dnd.handlers,
    onKeyDown: (id, event) => {
      // Alt+arrows move the row, the keyboard's drag and drop.
      if (event.altKey && event.key.startsWith("Arrow")) {
        event.preventDefault();
        const destination = keyboardMove(snapshot.entities, id, event.key);
        if (destination) moveTo([id], destination);
        return;
      }
      if (isContextMenuKey(event)) {
        event.preventDefault();
        const row = rows.find((candidate) => candidate.id === id);
        // Opened from the keyboard: under the focused row, which gets focus back.
        if (row) openMenu(row, menuPointFor(event.currentTarget));
        return;
      }
      const action = treeKeyAction(rows, id, event.key);
      if (!action) return;
      event.preventDefault();

      if ("focus" in action) {
        setFocused(action.focus);
        elements.focus(action.focus);
      } else if ("expand" in action) setOpen(action.expand, true);
      else if ("collapse" in action) setOpen(action.collapse, false);
      else if ("rename" in action) rename.start(action.rename);
      else select(action.select);
    },
    registerRow: elements.register,
  };
}

/** The row events a drag and drop needs. */
interface RowDragHandlers {
  readonly start: (id: EntityId, event: DragEvent<HTMLElement>) => void;
  readonly over: (id: EntityId, event: DragEvent<HTMLElement>) => void;
  readonly leave: (id: EntityId) => void;
  readonly drop: (id: EntityId, event: DragEvent<HTMLElement>) => void;
  readonly end: () => void;
}

/**
 * Drag and drop between rows. Dragging a selected row drags the selection, as
 * in Unreal. A row only accepts the drag (and shows an indicator) when the
 * drop would move something, so dropping into a dragged row's own subtree or
 * back where it came from isn't offered.
 */
function useRowDragAndDrop(
  entities: ReadonlyMap<EntityId, EntityRecord>,
  selection: SelectionState,
  moveTo: (ids: readonly EntityId[], destination: Destination) => void,
) {
  const dragged = useRef<readonly EntityId[]>([]);
  const [over, setOver] = useState<{ id: EntityId; zone: DropZone } | null>(null);

  /** Where a drop at this point of the row would go, or null if it would move nothing. */
  const destinationAt = (id: EntityId, event: DragEvent<HTMLElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const zone = dropZone(event.clientY - rect.top, rect.height);
    const destination = dropDestination(entities, id, zone);
    const edit = moveEdit(entities, dragged.current, destination, "outliner");
    return edit ? { zone, destination } : null;
  };

  const handlers: RowDragHandlers = {
    start: (id, event) => {
      dragged.current = actionTargets(selection, id);
      event.dataTransfer.effectAllowed = "move";
      // Firefox starts a drag only once something is set.
      event.dataTransfer.setData("text/plain", dragged.current.join("\n"));
    },
    over: (id, event) => {
      const target = destinationAt(id, event);
      if (!target) {
        if (over?.id === id) setOver(null);
        return;
      }
      event.preventDefault();
      event.dataTransfer.dropEffect = "move";
      if (over?.id !== id || over.zone !== target.zone) setOver({ id, zone: target.zone });
    },
    leave: (id) => {
      if (over?.id === id) setOver(null);
    },
    drop: (id, event) => {
      event.preventDefault();
      const target = destinationAt(id, event);
      if (target) moveTo(dragged.current, target.destination);
      dragged.current = [];
      setOver(null);
    },
    end: () => {
      dragged.current = [];
      setOver(null);
    },
  };

  return { over, handlers };
}

interface RowActions {
  readonly createChild: (id: EntityId) => void;
  /** Deletes the row (or the selection it's part of); null when it can't go (the level root). */
  readonly rename: (id: EntityId) => void;
  readonly setVisible: (id: EntityId, visible: boolean) => void;
  readonly frame: (id: EntityId) => void;
  readonly select: (id: EntityId) => void;
}

/**
 * A row's context menu: the Outliner's own actions, with their shortcuts.
 * With several entities selected (`count`), Hide and Frame take them all, and
 * the one-entity actions are disabled. The entity edits (Cut to Delete) come
 * last, as in every entity menu.
 */
function rowMenuItems(
  row: OutlinerRow,
  count: number,
  actions: RowActions,
  edits: EntityEdits,
  /** A camera's own actions (cameraMenuItems), right after Frame. */
  cameraItems: readonly MenuItem[],
): MenuItem[] {
  const hidden = row.visibility === "hidden";
  const several = count > 1;
  const { parent } = row;
  return [
    {
      label: "Rename",
      shortcut: "F2",
      disabled: several,
      run: () => {
        actions.rename(row.id);
      },
    },
    {
      label: hidden ? "Show" : "Hide",
      shortcut: "H",
      run: () => {
        actions.setVisible(row.id, hidden);
      },
    },
    {
      label: "Frame",
      shortcut: "F",
      run: () => {
        actions.frame(row.id);
      },
    },
    ...cameraItems,
    {
      label: "Select parent",
      disabled: parent === null || several,
      run: () => {
        if (parent !== null) actions.select(parent);
      },
    },
    {
      label: "Create empty child",
      run: () => {
        actions.createChild(row.id);
      },
    },
    ...entityEditItems(edits),
  ];
}

/** How long a second click waits before renaming, so a double-click can still frame (Windows' delay). */
const SLOW_CLICK_MS = 500;

/** Which row is being renamed, plus the pending slow-click timer. */
function useRenameState() {
  const [id, setId] = useState<EntityId | null>(null);
  const pending = useRef<{ id: EntityId; timer: ReturnType<typeof setTimeout> } | null>(null);

  const cancelPending = (keep: EntityId | null) => {
    if (!pending.current || pending.current.id === keep) return;
    clearTimeout(pending.current.timer);
    pending.current = null;
  };

  // A timer must not fire into an unmounted tree.
  useEffect(
    () => () => {
      if (pending.current) clearTimeout(pending.current.timer);
    },
    [],
  );

  return {
    id,
    cancelPending,
    start: (target: EntityId) => {
      cancelPending(null);
      setId(target);
    },
    startAfterPause: (target: EntityId) => {
      cancelPending(null);
      const timer = setTimeout(() => {
        pending.current = null;
        setId(target);
      }, SLOW_CLICK_MS);
      pending.current = { id: target, timer };
    },
    end: () => {
      setId(null);
    },
  };
}

/**
 * The row's own visibility (the entity's eye, like the Inspector's). It stays
 * out of the tab order so arrow keys keep moving between rows; H toggles the
 * active entity from the keyboard.
 */
function VisibilityEye({ row, tree }: { row: OutlinerRow; tree: OutlinerTree }) {
  const hidden = row.visibility === "hidden";
  const title =
    row.visibility === "inherited"
      ? "Hidden by a parent"
      : hidden
        ? "Show in viewport"
        : "Hide in viewport";

  return (
    <button
      type="button"
      tabIndex={-1}
      className="kn-outliner__eye"
      aria-label={`${hidden ? "Show" : "Hide"} ${row.name}`}
      aria-pressed={hidden}
      title={title}
      onClick={(event) => {
        // Toggling visibility doesn't select the row (Unreal behavior).
        event.stopPropagation();
        tree.setVisible(row.id, hidden);
      }}
      onDoubleClick={(event) => {
        event.stopPropagation();
      }}
    >
      {hidden ? <EyeOffIcon size={13} /> : <EyeIcon size={13} />}
    </button>
  );
}

/** Opens the ancestors of `active`, so a selection made in the viewport is visible here. */
function reveal(
  expanded: ReadonlySet<EntityId>,
  snapshot: Parameters<typeof ancestorsOf>[0],
  active: EntityId | null,
): ReadonlySet<EntityId> {
  if (active === null) return expanded;
  const hidden = ancestorsOf(snapshot, active).filter((id) => !expanded.has(id));
  return hidden.length === 0 ? expanded : new Set([...expanded, ...hidden]);
}

/** Row DOM elements, for moving keyboard focus and scrolling the selection into view. */
function useRowElements(active: EntityId | null) {
  const elements = useRef(new Map<EntityId, HTMLElement>());
  const pendingFocus = useRef<EntityId | null>(null);

  // After each render: apply a pending keyboard focus (the row may have just mounted).
  useEffect(() => {
    const id = pendingFocus.current;
    if (id === null) return;
    pendingFocus.current = null;
    elements.current.get(id)?.focus();
  });

  useEffect(() => {
    if (active === null) return;
    elements.current.get(active)?.scrollIntoView({ block: "nearest" });
  }, [active]);

  return {
    register: (id: EntityId, element: HTMLElement | null) => {
      if (element) elements.current.set(id, element);
      else elements.current.delete(id);
    },
    focus: (id: EntityId) => {
      const element = elements.current.get(id);
      if (element) element.focus();
      else pendingFocus.current = id;
    },
  };
}

// ─── Rows ────────────────────────────────────────────────────────────────

function OutlinerItem({ row, tree }: { row: OutlinerRow; tree: OutlinerTree }) {
  // Reparenting by drag is a mouse gesture: on touch, a drag scrolls the list.
  const pointer = usePointerKind();
  const Chevron = row.expanded ? ChevronDownIcon : ChevronRightIcon;
  const selected = tree.selected.has(row.id);

  return (
    <div
      ref={(element) => {
        tree.registerRow(row.id, element);
      }}
      role="treeitem"
      aria-label={row.name}
      aria-level={row.depth + 1}
      aria-selected={selected}
      {...(row.hasChildren && { "aria-expanded": row.expanded })}
      tabIndex={tree.focused === row.id ? 0 : -1}
      className="kn-outliner__row"
      data-icon={row.icon}
      data-match={row.matches || undefined}
      data-visibility={row.visibility}
      {...(tree.dropZoneOf(row.id) && { "data-drop": tree.dropZoneOf(row.id) })}
      draggable={pointer === "fine" && tree.renaming?.id !== row.id}
      onDragStart={(event) => {
        tree.drag.start(row.id, event);
      }}
      onDragOver={(event) => {
        tree.drag.over(row.id, event);
      }}
      onDragLeave={() => {
        tree.drag.leave(row.id);
      }}
      onDrop={(event) => {
        tree.drag.drop(row.id, event);
      }}
      onDragEnd={tree.drag.end}
      onClick={(event) => {
        tree.click(row.id, event);
      }}
      onDoubleClick={() => {
        tree.activate(row.id);
      }}
      onContextMenu={(event) => {
        event.preventDefault();
        tree.openMenu(row, { x: event.clientX, y: event.clientY });
      }}
      onFocus={() => {
        tree.setFocused(row.id);
      }}
      onKeyDown={(event) => {
        tree.onKeyDown(row.id, event);
      }}
    >
      <VisibilityEye row={row} tree={tree} />
      <span className="kn-outliner__indent" style={{ width: `${row.depth * 14}px` }} />
      <span
        className="kn-outliner__chevron"
        aria-hidden="true"
        onClick={(event) => {
          if (!row.hasChildren) return;
          // The chevron toggles without changing the selection (Unreal/Unity behavior).
          event.stopPropagation();
          tree.toggle(row.id);
        }}
      >
        {row.hasChildren && <Chevron size={10} />}
      </span>
      <EntityTypeIcon name={row.icon} size={13} className="kn-outliner__icon" />
      {tree.renaming?.id === row.id ? (
        <RenameField
          entity={tree.renaming}
          onDone={() => {
            tree.endRename(row.id);
          }}
        />
      ) : (
        <span
          className="kn-outliner__name"
          onClick={(event) => {
            // A second click on an already-selected row's name renames it.
            // Only a lone selection renames; with modifiers or several rows, it's selecting.
            const plain = !event.ctrlKey && !event.metaKey && !event.shiftKey;
            if (selected && plain && tree.selected.size === 1) tree.renameAfterPause(row.id);
          }}
        >
          {row.name}
        </span>
      )}
      {row.typeLabel && <span className="kn-outliner__type">{row.typeLabel}</span>}
    </div>
  );
}

/**
 * The in-place name editor: Enter commits (and stays open, with the reason,
 * if the name is rejected), Escape reverts, blur commits or reverts. Keys stay
 * in the field instead of moving through the tree.
 */
function RenameField({ entity, onDone }: { entity: EntityRecord; onDone: () => void }) {
  const { draft, setDraft, error, commit, revert } = useRename(entity, "outliner");
  const input = useRef<HTMLInputElement>(null);
  // Enter or Escape ends the edit; the blur that follows must not commit again.
  const finished = useRef(false);
  const finish = () => {
    finished.current = true;
    onDone();
  };

  useEffect(() => {
    input.current?.select();
  }, []);

  const errorId = `rename-error-${entity.id}`;
  return (
    <span className="kn-outliner__rename">
      <input
        ref={input}
        type="text"
        className="kn-input kn-outliner__rename-input"
        aria-label={`Rename ${entity.name}`}
        value={draft}
        autoFocus
        {...(error ? { "aria-invalid": true, "aria-describedby": errorId } : {})}
        onChange={(event) => {
          setDraft(event.target.value);
        }}
        onClick={(event) => {
          event.stopPropagation();
        }}
        onDoubleClick={(event) => {
          event.stopPropagation();
        }}
        onKeyDown={(event) => {
          event.stopPropagation();
          if (event.key === "Enter" && commit()) finish();
          if (event.key === "Escape") {
            revert();
            finish();
          }
        }}
        onBlur={() => {
          if (finished.current) return;
          if (!commit()) revert();
          finish();
        }}
      />
      {error && (
        <span id={errorId} className="kn-outliner__rename-error" role="alert">
          {error}
        </span>
      )}
    </span>
  );
}
